// frontend/src/features/equipment/EquipmentSetupWizard/components/ReviewList.tsx
import { Stack, Text, Title } from '@mantine/core';
import { CATEGORIES, SUBCATEGORIES } from '@/data/equipmentCatalogData';
import { ReviewListItem } from './ReviewListItem';
import type { PendingEquipment } from '../types';

type SessionSavedEquipment = PendingEquipment & { category?: string };

interface ReviewListProps {
  /** Equipment saved in this wizard session */
  sessionSavedEquipment: SessionSavedEquipment[];
}

/**
 * Displays a list of session-saved equipment grouped by category.
 * Shows empty state when no equipment is present.
 */
export function ReviewList({ sessionSavedEquipment }: ReviewListProps) {
  // Calculate total items (accounting for quantity)
  const totalItems = sessionSavedEquipment.reduce((sum, eq) => sum + eq.quantity, 0);

  // Group equipment by category
  const equipmentByCategory = new Map<string, SessionSavedEquipment[]>();

  for (const equipment of sessionSavedEquipment) {
    const subcategory = SUBCATEGORIES.find((s) => s.id === equipment.subcategoryId);
    const categoryId = equipment.category || subcategory?.categoryId || 'other';

    if (!equipmentByCategory.has(categoryId)) {
      equipmentByCategory.set(categoryId, []);
    }
    equipmentByCategory.get(categoryId)!.push(equipment);
  }

  // Get categories in catalog order, then include any unknown categories.
  const knownCategoryIds = CATEGORIES.map((cat) => cat.id).filter((categoryId) =>
    equipmentByCategory.has(categoryId)
  );
  const unknownCategoryIds = [...equipmentByCategory.keys()].filter(
    (categoryId) => !CATEGORIES.some((cat) => cat.id === categoryId)
  );
  const categoryIds = [...knownCategoryIds, ...unknownCategoryIds];

  if (sessionSavedEquipment.length === 0) {
    return (
      <Stack data-testid="review-list" align="center" py="xl">
        <Text c="dimmed">No equipment saved this session</Text>
      </Stack>
    );
  }

  return (
    <Stack data-testid="review-list" gap="lg">
      {categoryIds.map((categoryId) => {
        const categoryLabel =
          CATEGORIES.find((category) => category.id === categoryId)?.name || categoryId;
        const items = equipmentByCategory.get(categoryId) || [];
        return (
          <Stack key={categoryId} gap="xs">
            <Title order={6} c="dimmed" tt="uppercase">
              {categoryLabel}
            </Title>
            <Stack gap="xs">
              {items.map((equipment) => (
                <ReviewListItem key={equipment.tempId} equipment={equipment} />
              ))}
            </Stack>
          </Stack>
        );
      })}
      <Text size="sm" c="dimmed" ta="center">
        {totalItems} {totalItems === 1 ? 'item' : 'items'} saved this session
      </Text>
    </Stack>
  );
}
